import { useState } from 'react';

import { useCreateTerminalPaymentMutation } from '@/mutations';

import { useActionCable } from './useActionCable';

export const PAYMENT_STEP = {
  SENDING: 'sending',
  WAITING_FOR_TERMINAL: 'waiting_for_terminal',
  TERMINAL_FOUND: 'terminal_found',
  TERMINAL_NOT_FOUND: 'terminal_not_found',
  PAYMENT_DISPATCHED: 'payment_dispatched',
  PAYMENT_DISPATCH_FAILED: 'payment_dispatch_failed',
  PAYMENT_APPROVED: 'payment_approved',
  PAYMENT_DECLINED: 'payment_declined',
  CONNECTION_LOST: 'connection_lost',
  ERROR: 'error',
} as const;

export type PaymentStepKind = (typeof PAYMENT_STEP)[keyof typeof PAYMENT_STEP];

export type TerminalPaymentStep =
  | { kind: typeof PAYMENT_STEP.SENDING }
  | { kind: typeof PAYMENT_STEP.WAITING_FOR_TERMINAL }
  | { kind: typeof PAYMENT_STEP.TERMINAL_FOUND; ip?: string }
  | { kind: typeof PAYMENT_STEP.TERMINAL_NOT_FOUND }
  | { kind: typeof PAYMENT_STEP.PAYMENT_DISPATCHED }
  | { kind: typeof PAYMENT_STEP.PAYMENT_DISPATCH_FAILED; reason?: string }
  | { kind: typeof PAYMENT_STEP.PAYMENT_APPROVED; approvalCode?: string }
  | { kind: typeof PAYMENT_STEP.PAYMENT_DECLINED; reason?: string }
  | { kind: typeof PAYMENT_STEP.CONNECTION_LOST }
  | { kind: typeof PAYMENT_STEP.ERROR };

const TERMINAL_STEP_KINDS: PaymentStepKind[] = [
  PAYMENT_STEP.TERMINAL_NOT_FOUND,
  PAYMENT_STEP.PAYMENT_DISPATCH_FAILED,
  PAYMENT_STEP.PAYMENT_APPROVED,
  PAYMENT_STEP.PAYMENT_DECLINED,
];

// New step replaces the previous step in the array (instead of appending) when
// the new kind is the outcome of the previous kind's process state. Keeps the
// visible row count at exactly three.
const REPLACES: Partial<Record<PaymentStepKind, PaymentStepKind[]>> = {
  [PAYMENT_STEP.TERMINAL_NOT_FOUND]: [
    PAYMENT_STEP.SENDING,
    PAYMENT_STEP.WAITING_FOR_TERMINAL,
  ],
  [PAYMENT_STEP.PAYMENT_DISPATCH_FAILED]: [PAYMENT_STEP.TERMINAL_FOUND],
  [PAYMENT_STEP.PAYMENT_APPROVED]: [PAYMENT_STEP.PAYMENT_DISPATCHED],
  [PAYMENT_STEP.PAYMENT_DECLINED]: [PAYMENT_STEP.PAYMENT_DISPATCHED],
};

export const useTerminalPayment = () => {
  const [steps, setSteps] = useState<TerminalPaymentStep[]>([]);
  const { mutateAsync: createTerminalPayment } =
    useCreateTerminalPaymentMutation();

  const append = (step: TerminalPaymentStep) => {
    setSteps((prev) => {
      const last = prev[prev.length - 1];
      if (last && REPLACES[step.kind]?.includes(last.kind)) {
        return [...prev.slice(0, -1), step];
      }
      return [...prev, step];
    });
  };

  const { subscribe, disconnect } = useActionCable({
    onMessage: (data) => {
      const step = parseStep(data);
      if (!step) return;
      append(step);
      if (TERMINAL_STEP_KINDS.includes(step.kind)) disconnect();
    },
    onDisconnect: () => {
      setSteps((prev) => {
        const last = prev[prev.length - 1];
        if (last && TERMINAL_STEP_KINDS.includes(last.kind)) return prev;
        return [...prev, { kind: PAYMENT_STEP.CONNECTION_LOST }];
      });
    },
  });

  const sendToTerminal = async (
    url: string,
    amount: number,
    extraData?: Record<string, unknown>,
  ) => {
    setSteps([{ kind: PAYMENT_STEP.SENDING }]);

    const response = await createTerminalPayment({
      url,
      data: { amount, ...extraData },
    }).catch((error: unknown) => {
      console.error('Terminal payment failed', error);
      return null;
    });

    if (!response) {
      setSteps([{ kind: PAYMENT_STEP.ERROR }]);
      return;
    }

    setSteps([
      { kind: PAYMENT_STEP.SENDING },
      { kind: PAYMENT_STEP.WAITING_FOR_TERMINAL },
    ]);

    subscribe({
      channel: response.cable.channel,
      params: response.cable.params,
    });
  };

  const reset = () => {
    disconnect();
    setSteps([]);
  };

  const lastStep = steps[steps.length - 1];
  const isPending =
    lastStep != null &&
    !TERMINAL_STEP_KINDS.includes(lastStep.kind) &&
    lastStep.kind !== PAYMENT_STEP.ERROR &&
    lastStep.kind !== PAYMENT_STEP.CONNECTION_LOST;

  return {
    steps,
    isPending,
    sendToTerminal,
    reset,
  };
};

function parseStep(data: Record<string, unknown>): TerminalPaymentStep | null {
  const kind = data.kind;
  if (typeof kind !== 'string') return null;
  switch (kind) {
    case PAYMENT_STEP.TERMINAL_NOT_FOUND:
    case PAYMENT_STEP.PAYMENT_DISPATCHED:
      return { kind };
    case PAYMENT_STEP.TERMINAL_FOUND:
      return {
        kind,
        ip: typeof data.ip === 'string' ? data.ip : undefined,
      };
    case PAYMENT_STEP.PAYMENT_DISPATCH_FAILED:
      return {
        kind,
        reason: typeof data.reason === 'string' ? data.reason : undefined,
      };
    case PAYMENT_STEP.PAYMENT_APPROVED:
      return {
        kind,
        approvalCode:
          typeof data.approvalCode === 'string' ? data.approvalCode : undefined,
      };
    case PAYMENT_STEP.PAYMENT_DECLINED:
      return {
        kind,
        reason: typeof data.reason === 'string' ? data.reason : undefined,
      };
    default:
      return null;
  }
}
